import {
  Box, Container, Grid2 as Grid, Typography, Card, CardContent,
  Skeleton, Divider, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button
} from "@mui/material"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { fetchCommissionSummary, fetchAllPayouts, fetchFlaggedWallets } from "../../../redux/admin-slices/wallet.slice"
import { formatDate } from "../../../utils/helper"
import { useNavigate } from "react-router-dom"

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = "inherit", loading }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", height: "100%" }}>
      <CardContent>
        {loading ? (
          <>
            <Skeleton width={80} height={16} />
            <Skeleton width={120} height={40} sx={{ mt: 1 }} />
          </>
        ) : (
          <>
            <Typography fontSize={12} color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.8}>
              {label}
            </Typography>
            <Typography fontSize={28} fontWeight={800} color={color} mt={0.5}>
              {value}
            </Typography>
            {sub && (
              <Typography fontSize={12} color="text.secondary" mt={0.5}>{sub}</Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Payout Status Chip ───────────────────────────────────────────────────────
function PayoutChip({ status }) {
  const map = {
    succeeded: { label: "Succeeded", color: "success" },
    failed:    { label: "Failed",    color: "error"   },
    processing:{ label: "Processing",color: "warning" },
  }
  const cfg = map[status] || { label: status, color: "default" }
  return <Chip size="small" label={cfg.label} color={cfg.color} sx={{ fontWeight: 600, minWidth: 80 }} />
}

const fmt = (n, cur = "inr") => {
  const isUsd = cur?.toLowerCase() === "usd";
  const symbol = isUsd ? "$" : "₹";
  const locale = isUsd ? "en-US" : "en-IN";
  return `${symbol}${(n / 100).toLocaleString(locale, { minimumFractionDigits: 2 })}`;
}

/* ──────────────────────────────────────────────────────────────────────────── */

export default function WalletOverview() {
  const dispatch   = useDispatch()
  const navigate   = useNavigate()
  const [commission, setCommission] = useState(null)
  const [recentPayouts, setRecentPayouts] = useState([])
  const [flaggedCount, setFlaggedCount]   = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [commRes, payoutsRes, flaggedRes] = await Promise.all([
          dispatch(fetchCommissionSummary()).unwrap(),
          dispatch(fetchAllPayouts({ limit: 8 })).unwrap(),
          dispatch(fetchFlaggedWallets({ limit: 1 })).unwrap(),
        ])
        setCommission(commRes)
        setRecentPayouts(payoutsRes?.payouts ?? [])
        setFlaggedCount(flaggedRes?.pagination?.totalCount ?? 0)
      } catch { /* errors handled by toast */ }
      finally { setLoading(false) }
    }
    load()
    // eslint-disable-next-line
  }, [])

  const failedPayouts = recentPayouts.filter(p => p.status === "failed").length

  return (
    <Container maxWidth={false} fixed sx={{ py: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="pageTitle">Wallet Overview</Typography>
        <Typography color="text.secondary" fontSize={14} mt={0.5}>
          Platform-wide escrow summary. All funds are held on the platform until manually disbursed.
        </Typography>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          {
            label: "Total Gross Collected",
            value: commission ? fmt(commission.totalGross, "usd") : "—",
            sub: `${commission?.totalPayments ?? 0} payments`,
            color: "#1976d2",
          },
          {
            label: "Platform Commission",
            value: commission ? fmt(commission.totalCommission, "usd") : "—",
            sub: `Avg ${commission?.avgCommissionPct ?? 0}%`,
            color: "#388e3c",
          },
          {
            label: "Specialist Earnings",
            value: commission ? fmt(commission.totalSpecialist, "usd") : "—",
            sub: "Pending + Available + Paid Out",
            color: "#7b1fa2",
          },
          {
            label: "Flagged / Paused",
            value: loading ? "—" : flaggedCount,
            sub: flaggedCount > 0 ? "Requires attention" : "All clear",
            color: flaggedCount > 0 ? "#d32f2f" : "#388e3c",
          },
          {
            label: "Failed Payouts",
            value: loading ? "—" : failedPayouts,
            sub: failedPayouts > 0 ? "Retryable" : "No failures",
            color: failedPayouts > 0 ? "#d32f2f" : "#388e3c",
          },
        ].map((card, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, lg: 2.4 }}>
            <StatCard {...card} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* Recent Payouts */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography fontWeight={700} fontSize={16}>Recent Payouts</Typography>
          <Button size="small" onClick={() => navigate("/wallet/payouts")}>View All</Button>
        </Box>
        <Divider />
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "th": { fontWeight: 700, fontSize: 13, color: "#555" } }}>
                <TableCell>Specialist</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Transfer ID</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : recentPayouts.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No payouts yet.
                      </TableCell>
                    </TableRow>
                  )
                  : recentPayouts.map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {p.specialist?.fullName ?? "—"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {fmt(p.amount, p.currency)}
                      </TableCell>
                      <TableCell><PayoutChip status={p.status} /></TableCell>
                      <TableCell>
                        {p.stripeTransferId
                          ? (
                            <Typography
                              component="a"
                              href={`https://dashboard.stripe.com/transfers/${p.stripeTransferId}`}
                              target="_blank"
                              rel="noreferrer"
                              fontSize={12}
                              sx={{ fontFamily: "monospace", color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                            >
                              {p.stripeTransferId.substring(0, 20)}…
                            </Typography>
                          )
                          : <Typography fontSize={12} color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell><Typography fontSize={13}>{formatDate(p.createdAt)}</Typography></TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  )
}
